import React, { useEffect, useState } from "react";
import { getFieldMapping } from "../../services/webServices";
import {
  Box,
  Button,
  Modal,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Switch,
  Stack,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createTheme, styled, ThemeProvider } from "@mui/material/styles";
import "@fontsource/manrope";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AddIcon from "@mui/icons-material/Add";
import NewFieldMapper from "./NewFieldMapper";

const theme = createTheme({
  typography: {
    fontFamily: "Manrope, Arial, sans-serif",
  },
});

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#49A3FF",
        opacity: 1,
        border: 0,
        ...theme.applyStyles("dark", {
          backgroundColor: "#49A3FF",
        }),
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.grey[100],
      ...theme.applyStyles("dark", {
        color: theme.palette.grey[600],
      }),
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
      ...theme.applyStyles("dark", {
        opacity: 0.3,
      }),
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    ...theme.applyStyles("dark", {
      backgroundColor: "#39393D",
    }),
  },
}));

const getApiIssueType = (originalIssueType, tool) => {
  const toolNormalizations = {
    qTest: {
      "Test Cases": "test-cases",
      "Bugs": "defects",
    },
  };

  return toolNormalizations[tool]?.[originalIssueType] || originalIssueType;
};

const FieldMapper = ({
  importMapAvailable,
  oldMapping,
  setProceedingMap,
  setALMModalOpen,
  setPreviousSelection,
  toolName,
  projectName,
  issueType,
  taskType,
  getAvailableFields,
  qmentisFields,
  setOpenTemplatePage,

}) => {
  const [fieldMappings, setFieldMappings] = useState([]);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [useOldMapping, setUseOldMapping] = useState(false);

  const [openNewFieldMappingModal, setOpenNewFieldMappingModal] =
    useState(false);
  const [availableFields, setAvailableFields] = useState({});

  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newMapLoading, setNewMapLoading] = useState(false);

  useEffect(() => {
    const fieldMappingsGetter = async () => {
      try {
        setLoading(true);
        const apiIssueType = getApiIssueType(issueType, toolName);

        const getFieldMappings = await getFieldMapping({
          toolName,
          projectName,
          issueType: apiIssueType,
          taskType,
        });

        setFieldMappings(getFieldMappings.data);
      } catch (error) {
        setErrorMessage(
          error?.response?.data?.message ||
            "Unable to fetch field mappings. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    if (!openNewFieldMappingModal) {
      fieldMappingsGetter();
    }
  }, [openNewFieldMappingModal]);

  const handleBack = () => {
    setPreviousSelection(null); // Back to project selection
    setProceedingMap(null); // Reset proceeding map state
    // setOpenTemplatePage(true);
  };

  const handleCloseModal = () => {
    setALMModalOpen(false);
  };

  const handleOldMappingSwitch = (event) => {
    if (event.target.checked) {
      setSelectedMapping(oldMapping);
      setUseOldMapping(true);
    } else {
      setSelectedMapping(null);
      setUseOldMapping(false);
    }
  };

  const handleNewMappingClick = async () => {
    setNewMapLoading(true);
    try {
      const fields = await getAvailableFields();
      setAvailableFields(fields);
      setOpenNewFieldMappingModal(true);
    } catch (error) {
      console.log(error);
    } finally {
      setNewMapLoading(false);
    }
  };

  const handleProceed = () => {
    setProceedingMap(selectedMapping);
  };

 const apiIssueType = getApiIssueType(issueType, toolName);

  return (
    <ThemeProvider theme={theme}>
      {openNewFieldMappingModal ? (
        <NewFieldMapper
          issueType={apiIssueType}
          setALMModalOpen={setALMModalOpen}
          availableFields={availableFields}
          setAvailableFields={setAvailableFields}
          setOpenNewFieldMappingModal={setOpenNewFieldMappingModal}
          setSelectedMapping={setSelectedMapping}
          toolName={toolName}
          taskType={taskType}
          projectName={projectName}
          setProceedingMap={setProceedingMap}
          qmentisFields={qmentisFields}
        />
      ) : (
        <Modal open={true} onClose={handleCloseModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 500,
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 2,
              p: 3,
              outline: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between", // spread out left and right
                mb: 2,
              }}
            >
              {/* Left Side: Back Arrow and Title */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ArrowBackIosNewIcon
                  fontSize="small"
                  onClick={handleBack}
                  sx={{ mr: 1, cursor: "pointer" }}
                />
                <Typography variant="h6">Field Mapping</Typography>
              </Box>

              {/* Right Side: Close Button */}
              <IconButton
                onClick={handleCloseModal}
                aria-label="Close field mapping"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select saved field mapping or create New Mapping
            </Typography>

            {importMapAvailable && (
              <Stack
                direction="row"
                sx={{
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Use mapping used for import
                </Typography>
                <FormControlLabel
                  control={<IOSSwitch />}
                  checked={selectedMapping === oldMapping}
                  onChange={(event) => handleOldMappingSwitch(event)}
                />
              </Stack>
            )}

            {loading ? (
              <CircularProgress />
            ) : !!errorMessage ? (
              <Typography>{errorMessage}</Typography>
            ) : (<>
            {!useOldMapping && <Box
                sx={{ display: "flex", alignItems: "center", gap: 4, mb: 3 }}
              >
                <Typography sx={{ whiteSpace: "nowrap" }}>
                  Select Field Mapping
                </Typography>
                <FormControl size="medium" fullWidth>
                  <InputLabel>Choose a Mapping</InputLabel>
                  <Select
                    value={selectedMapping ? selectedMapping.mapping_name : ""}
                    onChange={(e) => {
                      const selected = fieldMappings.find(
                        (m) => m.mapping_name === e.target.value
                      );
                      setSelectedMapping(selected);
                    }}
                    label="Select Field Mapping"
                  >
                    {fieldMappings.map((mapping, index) => (
                      <MenuItem key={index} value={mapping.mapping_name}>
                        {mapping.mapping_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>}
            </>
              
            )}

            {/* Buttons */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <div>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#49A3FF",
                    color: "white",
                    textTransform: "none",
                  }}
                  disabled={newMapLoading}
                  onClick={handleNewMappingClick}
                >
                  {newMapLoading ? (
                    <CircularProgress />
                  ) : (
                    <>
                      <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Set New Map
                    </>
                  )}
                </Button>
              </div>

              <Button
                variant="contained"
                onClick={handleProceed}
                disabled={
                  selectedMapping === null || selectedMapping === undefined
                }
                sx={{
                  backgroundColor: "#49A3FF",
                  color: "white",
                  textTransform: "none",
                }}
              >
                Proceed
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </ThemeProvider>
  );
};

export default FieldMapper;
